import React, { useState } from 'react';
import { updateUserAPI } from '../services/serverService';

const GENDER_OPTIONS = [
    "Male", "Female", "Non-binary", "Agender", "Genderfluid", "Genderqueer",
    "Bigender", "Polygender", "Demiboy", "Demigirl", "Two-Spirit",
    "Androgynous", "Pangender", "Prefer not to say", "Other"
];

const ETHNICITY_OPTIONS = [
    "English, Welsh, Scottish, Northern Irish or British", "Irish",
    "Gypsy or Irish Traveller", "White and Black Caribbean", "White and Black African",
    "White and Asian", "Indian", "Pakistani", "Bangladeshi", "Chinese",
    "African", "Caribbean", "Arab", "Any other ethnic group"
];

export default function EditEmployeeForm({ employee, serverId, closeModal, onUpdate }) {
    // 1. HELPER: Convert "16-10-1991" (Firebase) -> "1991-10-16" (Input)
    const toInputDate = (dateStr) => {
        if (!dateStr) return "";
        // If it's already YYYY-MM-DD, leave it
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
        
        // Split DD-MM-YYYY or DD/MM/YYYY
        const parts = dateStr.split(/[-/]/);
        if (parts.length === 3) {
            // content is usually [day, month, year]
            // We want Year-Month-Day
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return "";
    };

    // 2. HELPER: Convert "1991-10-16" (Input) -> "16-10-1991" (Firebase)
    const toStorageDate = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr;
    };

    // Initialize state with formatted dates
    const [formData, setFormData] = useState({
        ...employee,
        phoneCode: employee.phoneCode || '+44',
        dob: toInputDate(employee.dob), 
        driversLicense: {
            ...employee.driversLicense,
            expiryDate: toInputDate(employee.driversLicense?.expiryDate)
        },
        dbs: {
            ...employee.dbs,
            expiryDate: toInputDate(employee.dbs?.expiryDate)
        }
    });

    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('personal');

    // Handle nested updates (e.g., address.town)
    const handleChange = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    // Handle top-level updates (e.g., fullName)
    const handleBasicChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Prepare the payload (Convert dates back to DD-MM-YYYY)
            const payload = {
                fullName: formData.fullName,
                dob: toStorageDate(formData.dob), 
                gender: formData.gender,
                nationality: formData.nationality,
                ethnicity: formData.ethnicity,
                niNumber: formData.niNumber,
                phoneCode: formData.phoneCode, 
                phoneNumber: formData.phoneNumber,
                // Nested Objects
                address: formData.address || {},
                nextOfKin: formData.nextOfKin || {},
                bankDetails: formData.bankDetails || {},
                // Handle License Date Logic
                driversLicense: {
                   ...formData.driversLicense,
                   expiryDate: toStorageDate(formData.driversLicense?.expiryDate)
                },
                dbs: {
                    ...formData.dbs,
                    expiryDate: toStorageDate(formData.dbs?.expiryDate)
                }
            };

            // ID Priority: username > id > uid > email (Prevents SSO collision)
            const targetId = employee.username || employee.id || employee.uid || employee.email;
            
            await updateUserAPI(targetId, {
                ...payload,
                serverId: serverId,
                role: 'employee'
            });
            
            alert("Employee updated successfully!");
            onUpdate(); 
            closeModal();
        } catch (error) {
            console.error("Error updating employee:", error);
            // Handle error format from the global API
            alert("Failed to update: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // Styles
    const inputStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '10px', boxSizing: 'border-box' };
    const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#555' };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', width: '90%', maxWidth: '700px', height: '85%', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 5px 15px rgba(0,0,0,0.2)' }}>
                {/* Header */}
                <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>Edit Employee Profile</h2>
                    <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999' }}>×</button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', background: '#f8f9fa', padding: '0 10px', borderBottom: '1px solid #eee', overflowX: 'auto' }}>
                    {['personal', 'address', 'kin', 'bank', 'compliance'].map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '15px 20px', border: 'none', cursor: 'pointer', background: 'transparent', borderBottom: activeTab === tab ? '3px solid #0056b3' : '3px solid transparent', fontWeight: activeTab === tab ? 'bold' : '500', color: activeTab === tab ? '#0056b3' : '#666', whiteSpace: 'nowrap' }}>
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Form Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '25px' }}>
                    <form id="editForm" onSubmit={handleSubmit}>
                        
                        {/* 1. Personal Tab */}
                        <div style={{ display: activeTab === 'personal' ? 'block' : 'none' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div><label style={labelStyle}>Full Name</label><input style={inputStyle} value={formData.fullName || ''} onChange={(e) => handleBasicChange('fullName', e.target.value)} /></div>
                                
                                <div>
                                    <label style={labelStyle}>Phone Number</label>
                                    <div style={{ display: 'flex', gap: '5px' }}>
                                        <input 
                                            type="text"
                                            style={{...inputStyle, width: '60px', marginBottom: 0}} 
                                            value={formData.phoneCode || '+44'} 
                                            onChange={(e) => handleBasicChange('phoneCode', e.target.value)} 
                                            placeholder="+44"
                                        />
                                        <input style={{...inputStyle, flex: 1}} value={formData.phoneNumber || ''} onChange={(e) => handleBasicChange('phoneNumber', e.target.value)} placeholder="7575944496" />
                                    </div>
                                </div>
                                <div><label style={labelStyle}>Date of Birth</label><input type="date" style={inputStyle} value={formData.dob || ''} onChange={(e) => handleBasicChange('dob', e.target.value)} /></div>
                                <div>
                                    <label style={labelStyle}>Gender</label>
                                    <select style={inputStyle} value={formData.gender || ''} onChange={(e) => handleBasicChange('gender', e.target.value)}>
                                        <option value="">Select Gender Identity</option>
                                        {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                </div>
                                <div><label style={labelStyle}>Nationality</label><input style={inputStyle} value={formData.nationality || ''} onChange={(e) => handleBasicChange('nationality', e.target.value)} /></div>
                                <div>
                                    <label style={labelStyle}>Ethnicity</label>
                                    <select style={inputStyle} value={formData.ethnicity || ''} onChange={(e) => handleBasicChange('ethnicity', e.target.value)}>
                                        <option value="">Select Ethnicity</option>
                                        {ETHNICITY_OPTIONS.map(eth => <option key={eth} value={eth}>{eth}</option>)}
                                    </select>
                                </div>
                                <div><label style={labelStyle}>NI Number</label><input style={inputStyle} value={formData.niNumber || ''} onChange={(e) => handleBasicChange('niNumber', e.target.value)} /></div>
                            </div>
                        </div>

                        {/* 2. Address Tab - DECONSOLIDATED */}
                        <div style={{ display: activeTab === 'address' ? 'block' : 'none' }}>
                             <label style={labelStyle}>Flat / Building Number / Name</label><input style={inputStyle} value={formData.address?.secondLineOfAddress || ''} onChange={(e) => handleChange('address', 'secondLineOfAddress', e.target.value)} placeholder="e.g. Flat 5" />
                             <label style={labelStyle}>Street / First Line</label><input style={inputStyle} value={formData.address?.firstLineOfAddress || ''} onChange={(e) => handleChange('address', 'firstLineOfAddress', e.target.value)} placeholder="e.g. 11 Jubilee Rd" />
                             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div><label style={labelStyle}>Town / City</label><input style={inputStyle} value={formData.address?.town || ''} onChange={(e) => handleChange('address', 'town', e.target.value)} /></div>
                                <div><label style={labelStyle}>County</label><input style={inputStyle} value={formData.address?.county || ''} onChange={(e) => handleChange('address', 'county', e.target.value)} /></div>
                                <div><label style={labelStyle}>Postcode</label><input style={inputStyle} value={formData.address?.postalCode || ''} onChange={(e) => handleChange('address', 'postalCode', e.target.value)} /></div>
                                <div><label style={labelStyle}>Country</label><input style={inputStyle} value={formData.address?.country || ''} onChange={(e) => handleChange('address', 'country', e.target.value)} /></div>
                            </div>
                        </div>

                        {/* 3. Next of Kin Tab */}
                        <div style={{ display: activeTab === 'kin' ? 'block' : 'none' }}>
                            <label style={labelStyle}>Full Name</label><input style={inputStyle} value={formData.nextOfKin?.name || ''} onChange={(e) => handleChange('nextOfKin', 'name', e.target.value)} />
                            <label style={labelStyle}>Relationship</label><input style={inputStyle} value={formData.nextOfKin?.relation || ''} onChange={(e) => handleChange('nextOfKin', 'relation', e.target.value)} />
                            <label style={labelStyle}>Phone Number</label><input style={inputStyle} value={formData.nextOfKin?.phone || ''} onChange={(e) => handleChange('nextOfKin', 'phone', e.target.value)} />
                            <label style={labelStyle}>Email Address</label><input style={inputStyle} value={formData.nextOfKin?.email || ''} onChange={(e) => handleChange('nextOfKin', 'email', e.target.value)} />
                            <label style={labelStyle}>Address</label><input style={inputStyle} value={formData.nextOfKin?.address || ''} onChange={(e) => handleChange('nextOfKin', 'address', e.target.value)} />
                        </div>

                        {/* 4. Bank Tab */}
                        <div style={{ display: activeTab === 'bank' ? 'block' : 'none' }}>
                            <label style={labelStyle}>Bank Name</label><input style={inputStyle} value={formData.bankDetails?.bank || ''} onChange={(e) => handleChange('bankDetails', 'bank', e.target.value)} />
                            <label style={labelStyle}>Account Holder Name</label><input style={inputStyle} value={formData.bankDetails?.accountName || ''} onChange={(e) => handleChange('bankDetails', 'accountName', e.target.value)} />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div><label style={labelStyle}>Account Number</label><input style={inputStyle} value={formData.bankDetails?.accountNumber || ''} onChange={(e) => handleChange('bankDetails', 'accountNumber', e.target.value)} /></div>
                                <div><label style={labelStyle}>Sort Code</label><input style={inputStyle} value={formData.bankDetails?.sortCode || ''} onChange={(e) => handleChange('bankDetails', 'sortCode', e.target.value)} /></div>
                            </div>
                        </div>

                        {/* 5. Compliance Tab */}
                        <div style={{ display: activeTab === 'compliance' ? 'block' : 'none' }}>
                            <label style={labelStyle}>DBS Expiry Date</label><input type="date" style={inputStyle} value={formData.dbs?.expiryDate || ''} onChange={(e) => handleChange('dbs', 'expiryDate', e.target.value)} />
                            <label style={labelStyle}>Driving License Expiry Date</label><input type="date" style={inputStyle} value={formData.driversLicense?.expiryDate || ''} onChange={(e) => handleChange('driversLicense', 'expiryDate', e.target.value)} />
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div style={{ padding: '20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#f8f9fa' }}>
                    <button onClick={closeModal} style={{ padding: '10px 20px', border: '1px solid #ccc', borderRadius: '4px', background: 'white', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
                    <button type="submit" form="editForm" disabled={loading} style={{ padding: '10px 20px', border: 'none', borderRadius: '4px', background: '#0056b3', color: 'white', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}